/**
 * Which category an attachment belongs to (WPS-140 #1): the claim Evidence
 * card's sections, and the shared Documents card's (property, transfer).
 *
 * The platform records the same idea in more than one column because the two
 * upload paths were built separately: an evidence-request upload carries the
 * packet's `requirement_key` ("slope_photos"), while a manual upload writes
 * `category`. `file_type` is the media type and is always set.
 *
 * The resolution order is deliberate:
 *
 *   1. `requirement_key` - the packet vocabulary, always a clean key, and the
 *      same vocabulary the review checklist matches on.
 *   2. `category` - but a media word here ("photo") is a media type recorded in
 *      the wrong column; `file_type` already holds that, so it is not a
 *      taxonomy value and must not become a bucket.
 *   3. `document_option_key`.
 *   4. the media type, so a tenant that categorises nothing still gets useful
 *      groups (Photos / Documents / Videos) rather than one "Uncategorised" pile.
 *
 * Keys are normalised because the same category reaches us both as a key and
 * as a label ("slope_photos" and "Slope Photos"), which would otherwise render
 * as two separate groups.
 */

/** An attachment, narrowed to the fields grouping reads. */
export interface GroupableAttachment {
  category?: string
  requirement_key?: string
  document_option_key?: string
  file_type?: string
}

/** Prefix marking a group that fell back to the media type rather than a real category. */
export const MEDIA_GROUP_PREFIX = 'media:'

/**
 * Values that appear in `category` but describe the medium, not the evidence.
 * `file_type` is the field that means this, so they never form a group.
 */
const MEDIA_WORDS = new Set([
  'photo',
  'photos',
  'image',
  'images',
  'document',
  'documents',
  'video',
  'videos',
  'audio',
  'file',
  'files',
  // Not 'other': "Other" is a real category the client files evidence under -
  // a note's attachments land on the record as Other (WPS-153) - so it gets
  // its own section rather than dissolving into Photos / Documents.
])

/** Trim, lower-case, and collapse spaces to underscores. */
export function normaliseCategoryKey(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, '_')
}

/** The group an attachment belongs to. Never empty. */
export function resolveEvidenceCategoryKey(att: GroupableAttachment): string {
  const requirement = normaliseCategoryKey(att.requirement_key)
  if (requirement) return requirement

  const category = normaliseCategoryKey(att.category)
  if (category && !MEDIA_WORDS.has(category)) return category

  const documentOption = normaliseCategoryKey(att.document_option_key)
  if (documentOption) return documentOption

  return MEDIA_GROUP_PREFIX + (normaliseCategoryKey(att.file_type) || 'other')
}

/** Human label for a media-type fallback group. */
const MEDIA_GROUP_LABEL: Record<string, string> = {
  photo: 'Photos',
  document: 'Documents',
  video: 'Videos',
  audio: 'Audio',
  other: 'Other files',
}

/**
 * Display label for a group key.
 *
 * On a claim, `labelsByKey` comes from the claim's checklist - the same packet
 * config the evidence-request email is built from - so a category reads exactly
 * as it does to the claimant ("Slope and close-up photos"). On the Documents
 * card it holds the labels people typed. Anything else falls back to a
 * humanised key rather than showing a raw slug.
 */
export function evidenceGroupLabel(
  key: string,
  labelsByKey: Map<string, string>,
): string {
  const configured = labelsByKey.get(key)
  if (configured) return configured

  if (key.startsWith(MEDIA_GROUP_PREFIX)) {
    const media = key.slice(MEDIA_GROUP_PREFIX.length)
    return MEDIA_GROUP_LABEL[media] ?? MEDIA_GROUP_LABEL.other
  }

  return key
    .split('_')
    .filter(Boolean)
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word,
    )
    .join(' ')
}
